import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SoloShell from './SoloShell';
import { api } from './api';
import './BuzzerSolo.css';

export default function BuzzerSoloTablesPage() {
  const [areas, setAreas] = useState([]);
  const [allowedAreaIds, setAllowedAreaIds] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [tables, setTables] = useState([]);
  const [message, setMessage] = useState('');
  const [newArea, setNewArea] = useState('');
  const [singleTable, setSingleTable] = useState({ number: '', capacity: '', comment: '' });
  const [bulk, setBulk] = useState({ from: '', to: '', capacity: '' });

  const load = useCallback(async () => {
    try {
      const [areasRes, tablesRes] = await Promise.all([
        api.get('/buzzersolo/areas'),
        api.get('/buzzersolo/tables'),
      ]);
      setAreas(areasRes.data.areas || []);
      setAllowedAreaIds(areasRes.data.allowedAreaIds || []);
      setTables(tablesRes.data || []);
      setSelectedArea((prev) => prev || areasRes.data.areas?.[0]?.id || null);
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to load Solo setup');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleTables = useMemo(() => tables.filter((t) => !selectedArea || Number(t.area) === Number(selectedArea)), [tables, selectedArea]);

  const createArea = async () => {
    if (!newArea.trim()) return;
    try {
      await api.post('/buzzersolo/areas', { name: newArea.trim() });
      setNewArea('');
      setMessage('Area created.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to create area');
    }
  };

  const createSingleTable = async () => {
    if (!selectedArea) return;
    try {
      await api.post('/buzzersolo/tables', {
        area: selectedArea,
        number: singleTable.number,
        capacity: singleTable.capacity,
        comment: singleTable.comment,
      });
      setSingleTable({ number: '', capacity: '', comment: '' });
      setMessage('Table created.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to create table');
    }
  };

  const createBulkTables = async () => {
    if (!selectedArea) return;
    try {
      await api.post('/buzzersolo/tables/bulk-create', {
        area: selectedArea,
        from: Number(bulk.from),
        to: Number(bulk.to),
        capacity: bulk.capacity,
      });
      setBulk({ from: '', to: '', capacity: '' });
      setMessage('Bulk tables created.');
      await load();
    } catch (error) {
      setMessage(error?.response?.data?.error || 'Failed to bulk create tables');
    }
  };

  return (
    <SoloShell
      title="Areas & Tables"
      subtitle="Use this simple setup flow instead of the main reservations layout editor."
      actions={<button className="bsolo-btn-secondary" onClick={load}>Refresh</button>}
    >
      {message ? <div className="bsolo-card" style={{ marginBottom: 16 }}>{message}</div> : null}

      <div className="bsolo-grid cols-2">
        <div className="bsolo-stack">
          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Areas</h3>
            <div className="bsolo-inline-form">
              <div className="bsolo-field">
                <label>New area name</label>
                <input value={newArea} onChange={(e) => setNewArea(e.target.value)} placeholder="e.g. Roof Garden" />
              </div>
              <div style={{ alignSelf: 'end' }}>
                <button className="bsolo-btn" onClick={createArea}>Add area</button>
              </div>
            </div>
            <div className="bsolo-tabs" style={{ marginTop: 16 }}>
              {areas.map((area) => (
                <button key={area.id} className={`bsolo-tab ${Number(selectedArea) === Number(area.id) ? 'active' : ''}`} onClick={() => setSelectedArea(area.id)}>
                  {area.name}
                </button>
              ))}
            </div>
            <div className="bsolo-meta">Visible areas for your user: {allowedAreaIds.join(', ') || 'none'}</div>
          </div>

          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Create one table</h3>
            <div className="bsolo-inline-form">
              <div className="bsolo-field"><label>Number</label><input value={singleTable.number} onChange={(e) => setSingleTable((s) => ({ ...s, number: e.target.value }))} /></div>
              <div className="bsolo-field"><label>Seats</label><input value={singleTable.capacity} onChange={(e) => setSingleTable((s) => ({ ...s, capacity: e.target.value }))} /></div>
              <div className="bsolo-field"><label>Comment</label><input value={singleTable.comment} onChange={(e) => setSingleTable((s) => ({ ...s, comment: e.target.value }))} /></div>
            </div>
            <div className="bsolo-actions"><button className="bsolo-btn" onClick={createSingleTable}>Create table</button></div>
          </div>

          <div className="bsolo-panel">
            <h3 style={{ marginTop: 0 }}>Quick generate</h3>
            <div className="bsolo-inline-form">
              <div className="bsolo-field"><label>From</label><input value={bulk.from} onChange={(e) => setBulk((s) => ({ ...s, from: e.target.value }))} /></div>
              <div className="bsolo-field"><label>To</label><input value={bulk.to} onChange={(e) => setBulk((s) => ({ ...s, to: e.target.value }))} /></div>
              <div className="bsolo-field"><label>Seats (optional)</label><input value={bulk.capacity} onChange={(e) => setBulk((s) => ({ ...s, capacity: e.target.value }))} /></div>
            </div>
            <div className="bsolo-actions"><button className="bsolo-btn" onClick={createBulkTables}>Generate tables</button></div>
          </div>
        </div>

        <div className="bsolo-panel">
          <h3 style={{ marginTop: 0 }}>Tables in selected area</h3>
          <div className="bsolo-list">
            {visibleTables.map((table) => (
              <div key={table.id} className="bsolo-card" style={{ padding: 14 }}>
                <div className="bsolo-row" style={{ justifyContent: 'space-between' }}>
                  <strong>Table {table.number}</strong>
                  <span className="bsolo-meta">{table.capacity ? `${table.capacity} seats` : 'No seat limit'}</span>
                </div>
                {table.comment ? <div className="bsolo-meta" style={{ marginTop: 6 }}>{table.comment}</div> : null}
              </div>
            ))}
            {!visibleTables.length && <div className="bsolo-empty">No tables in this area yet.</div>}
          </div>
        </div>
      </div>
    </SoloShell>
  );
}
